import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, Checkbox, Row, Col, Alert, Card, Typography, message } from 'antd';
import { useUpdateQuestionTemplateMutation } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { IQuestionTemplate, IUpdateQuestionTemplate } from '../../services/nestjs-base-api/types';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface QuestionTemplateEditModalProps {
    visible: boolean;
    template: IQuestionTemplate;
    onClose: () => void;
    onSuccess: () => void;
}

const QuestionTemplateEditModal: React.FC<QuestionTemplateEditModalProps> = ({
    visible,
    template,
    onClose,
    onSuccess,
}) => {
    const { user } = useAuth();
    const [form] = Form.useForm();
    const [previewBrand, setPreviewBrand] = useState('Example Brand');
    const [useBrandPlaceholder, setUseBrandPlaceholder] = useState(template.useBrandPlaceholder);
    
    const [updateTemplate, { isLoading }] = useUpdateQuestionTemplateMutation();

    useEffect(() => {
        if (template) {
            form.setFieldsValue({
                text: template.text,
                category: template.category,
                displayOrder: template.displayOrder,
                isCore: template.isCore,
                isRequired: template.isRequired,
                questionTypeId: template.questionTypeId,
                useBrandPlaceholder: template.useBrandPlaceholder,
                status: template.status,
            });
            setUseBrandPlaceholder(template.useBrandPlaceholder);
        }
    }, [template, form]);

    const handleSubmit = async (values: any) => {
        try {
            const payload: IUpdateQuestionTemplate = {
                text: values.text,
                category: values.category,
                displayOrder: values.displayOrder,
                isCore: values.isCore,
                isRequired: values.isRequired,
                questionTypeId: values.questionTypeId || 1,
                useBrandPlaceholder: values.useBrandPlaceholder,
                status: values.status,
            };

            await updateTemplate({
                jwtToken: user?.jwtToken || '',
                id: template.id,
                payload,
            }).unwrap();

            onSuccess();
        } catch (error) {
            console.error('Failed to update question template:', error);
            message.error('Failed to update question template');
        }
    };

    const validateQuestionText = (_: any, value: string) => {
        if (useBrandPlaceholder && !value.includes('{{brand}}')) {
            return Promise.reject(new Error('Question must contain {{brand}} placeholder when "Use Brand Placeholder" is checked'));
        }
        return Promise.resolve();
    };

    const renderPreview = () => {
        const text = form.getFieldValue('text') || '';
        const processedText = useBrandPlaceholder ? text.replace(/\{\{brand}}/g, previewBrand) : text;
        
        return (
            <Card size="small" style={{ backgroundColor: '#f5f5f5' }}>
                <Text strong>Preview:</Text>
                <div style={{ marginTop: 8 }}>{processedText}</div>
            </Card>
        );
    };

    const categories = [
        'Loyalty',
        'Product Quality',
        'Brand Awareness',
        'Brand Association',
        'Customer Satisfaction',
        'Purchase Intention',
        'Value for Money',
        'Brand Trust',
        'Overall Brand Equity',
    ];

    return (
        <Modal
            title={`Edit Question Template - ${template.code}`}
            open={visible}
            onCancel={onClose}
            onOk={form.submit}
            width={800}
            confirmLoading={isLoading}
            okText="Save Changes"
        >
            {template.isCore && (
                <Alert
                    message="Core Question"
                    description="This is a core question that will be included in all survey rotations. Changes will affect all future surveys."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    label="Question Text"
                    name="text"
                    rules={[
                        { required: true, message: 'Please enter question text' },
                        { validator: validateQuestionText },
                    ]}
                >
                    <TextArea
                        rows={3}
                        placeholder="Enter question text. Use {{brand}} where the brand name should appear."
                    />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Category"
                            name="category"
                            rules={[{ required: true, message: 'Please select a category' }]}
                        >
                            <Select placeholder="Select category">
                                {categories.map(cat => (
                                    <Option key={cat} value={cat}>{cat}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Display Order"
                            name="displayOrder"
                            rules={[{ required: true, message: 'Please enter display order' }]}
                        >
                            <InputNumber min={1} max={100} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="isCore"
                            valuePropName="checked"
                        >
                            <Checkbox>Core Question</Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="isRequired"
                            valuePropName="checked"
                        >
                            <Checkbox>Required</Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="useBrandPlaceholder"
                            valuePropName="checked"
                        >
                            <Checkbox
                                onChange={(e) => setUseBrandPlaceholder(e.target.checked)}
                            >
                                Use Brand Placeholder
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Status"
                            name="status"
                            rules={[{ required: true, message: 'Please select status' }]}
                        >
                            <Select>
                                <Option value="active">Active</Option>
                                <Option value="inactive">Inactive</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Preview Brand Name"
                        >
                            <Input
                                value={previewBrand}
                                onChange={(e) => setPreviewBrand(e.target.value)}
                                placeholder="Enter brand name for preview"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {renderPreview()}
            </Form>
        </Modal>
    );
};

export default QuestionTemplateEditModal;